import '@theme/storefront-loading';
/** Opening cover: mount one media path after native menu enhancement, before features. */
class ProductOverlayHero extends HTMLElement {
  #controller;
  #frame = 0;
  #loadingTimer = 0;
  #canvas;
  #logo;
  #background;
  #reduced = matchMedia('(prefers-reduced-motion: reduce)');
  #connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  #constrained = false;
  #mediaVersion = 0;
  #introFinished = false;
  #introFrame = 0;
  #introTimer = 0;
  #softBackground;
  #lastProgress = null;
  #pendingFlat = false;
  #idle = null;
  #idleFallback = 0;
  #revealAnimations = [];
  #replayAnimations = [];
  #replaying = false;
  #replayVersion = 0;

  connectedCallback() {
    // A localization navigation keeps the finished brand composition. Its
    // imagery still waits for decoding, but the logo is not constructed again.
    if (document.documentElement.hasAttribute('data-localization-arrival')) this.#introFinished = true;
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.#canvas = this.querySelector('.product-overlay-hero__canvas');
    if (!this.#canvas) return;
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) { this.#finishIntro(); this.#cancelReveal(); }
    }, { signal });
    window.addEventListener('pagehide', () => { this.#finishIntro(); this.#cancelReveal(); }, { signal });
    window.addEventListener('pageshow', event => {
      // BFCache restores already-loaded artwork without remounting the element.
      // Reveal it again, but never restart the SVG construction sequence.
      if (event.persisted) this.#revealCover();
    }, { signal });
    this.#reduced.addEventListener('change', () => {
      if (this.#reduced.matches) this.#cancelReveal();
    }, { signal });
    this.addEventListener('animationend', event => {
      if (!this.#replaying && event.animationName === 'bardem-subtitle-enter') this.#finishIntro();
    }, { signal });
    this.addEventListener('click', event => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const link = event.target.closest('[data-floating-logo][data-replay-logo]');
      if (!link || !this.contains(link)) return;
      event.preventDefault();
      this.#replayIntro();
    }, { signal });
    this.#constrained = this.#isConstrained();
    if ((this.dataset.flatMode === 'always' || this.#constrained) && this.#showFlat()) return;
    this.#showLayers();

    // Listen to both scroll surfaces: the desktop wrapper and the document.
    window.addEventListener('scroll', this.#schedule, { passive: true, signal });
    this.closest('.page-wrapper')?.addEventListener('scroll', this.#schedule, { passive: true, signal });
    window.addEventListener('resize', this.#schedule, { passive: true, signal });
    this.#reduced.addEventListener('change', this.#schedule, { signal });
    this.#connection?.addEventListener('change', this.#onConnectionChange, { signal });
    this.#schedule();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    cancelAnimationFrame(this.#frame);
    clearTimeout(this.#loadingTimer);
    this.#finishIntro();
    this.#cancelReveal();
    this.#frame = 0;
  }

  #isConstrained() {
    return !!(this.#connection?.saveData || ['slow-2g', '2g', '3g'].includes(this.#connection?.effectiveType));
  }

  #onConnectionChange = () => {
    this.#constrained = this.#isConstrained();
    if (this.#constrained) {
      if (this.hasAttribute('data-building')) this.#pendingFlat = true;
      else this.#showFlat();
    }
    this.#schedule();
  };

  #clearMedia() {
    this.#cancelReplay();
    this.#cancelReveal();
    this.#mediaVersion++;
    clearTimeout(this.#loadingTimer);
    this.#canvas.querySelectorAll(':scope > :not(template):not(noscript):not([data-cover-skeleton])').forEach(node => node.remove());
    this.#logo = null;
    this.#background = null;
    this.#softBackground = null;
    this.#lastProgress = null;
    cancelAnimationFrame(this.#introFrame);
    clearTimeout(this.#introTimer);
    this.#cancelIdle();
    this.removeAttribute('data-intro-pending');
    this.removeAttribute('data-building');
  }

  #showFlat() {
    const template = this.querySelector('template[data-flat-cover]');
    if (!template) return false;
    if (this.hasAttribute('data-flat')) return true;
    const fragment = template.content.cloneNode(true);
    const image = fragment.querySelector('img');
    const version = this.#mediaVersion;
    const mount = () => {
      if (!this.isConnected || version !== this.#mediaVersion) return;
      if (this.hasAttribute('data-building')) { this.#pendingFlat = true; return; }
      this.#clearMedia();
      this.#introFinished = true;
      this.setAttribute('data-flat', '');
      this.#canvas.append(fragment);
      return true;
    };
    // Retain painted artwork until its replacement is decoded. On initial
    // data-saver entry there are no heavy layers to download or keep around.
    if (this.#background) {
      void this.#decode(image).then(() => {
        if (mount()) { this.#markReady(); this.#finishIntro(); }
      }).catch(() => {
        if (!this.isConnected || version !== this.#mediaVersion) return;
        this.#markReady(); this.#finishIntro();
      });
    } else {
      mount();
      void this.#decode(image).then(() => {
        if (!image?.isConnected || !this.isConnected) return;
        this.#markReady(); this.#finishIntro();
      }).catch(() => {
        if (image?.isConnected && this.isConnected) this.#showLayers(false);
      });
    }
    return true;
  }

  #showLayers(allowFallback = true) {
    const template = this.querySelector('template[data-layered-cover]');
    if (!template) return;
    this.#clearMedia();
    this.removeAttribute('data-flat');
    this.setAttribute('data-cover-loading', '');
    this.setAttribute('aria-busy', 'true');
    // Reserve the logo's final space but do not start its clock while decoding.
    this.toggleAttribute('data-intro-pending', !this.#introFinished && this.dataset.animateLogo === 'true' && !this.#constrained && !this.#reduced.matches && !document.hidden);
    this.#canvas.append(template.content.cloneNode(true));
    this.#logo = this.querySelector('[data-floating-logo]');
    this.#background = this.querySelector('.product-overlay-hero__background-media');
    const images = [...this.#canvas.querySelectorAll('img')].filter(image => !image.closest('[data-cover-skeleton]'));
    const version = this.#mediaVersion;
    const failed = () => {
      if (!this.isConnected || version !== this.#mediaVersion) return;
      clearTimeout(this.#loadingTimer);
      this.#finishIntro();
      if (!allowFallback || !this.#showFlat()) this.#markReady();
    };
    this.#loadingTimer = setTimeout(() => {
      failed();
    }, 2000);
    const fonts = document.fonts ? Promise.race([document.fonts.ready, new Promise(resolve => setTimeout(resolve, 1200))]) : Promise.resolve();
    void Promise.all(images.map(image => this.#decode(image))).then(async () => {
      if (!this.isConnected || version !== this.#mediaVersion) return;
      clearTimeout(this.#loadingTimer);
      // A decoded cover should not remain behind a skeleton while fonts or
      // the logo's idle-start barrier settle. Keep those animation safeguards.
      this.#markReady();
      await fonts;
      if (!this.isConnected || version !== this.#mediaVersion) return;
      if (!this.hasAttribute('data-intro-pending')) { this.#finishIntro(); return; }
      if (document.hidden || this.#reduced.matches) { this.#finishIntro(); return; }
      const effects = this.querySelector('.product-overlay-hero__background-effects');
      if (this.#background && effects && Number(this.dataset.backgroundBlur) > 0) {
        this.#softBackground = this.#background.cloneNode();
        this.#softBackground.classList.add('product-overlay-hero__background-soft');
        this.#softBackground.alt = '';
        this.#softBackground.setAttribute('aria-hidden', 'true');
        // Blur and tint are composited together, with one shared opacity clock.
        effects.prepend(this.#softBackground);
      }
      // Let critical menu/initialization work drain before the first stroke.
      // The wait is bounded; it cannot leave the intro queued indefinitely.
      const begin = () => {
        this.#idle = null;
        this.#introFrame = requestAnimationFrame(() => {
          this.#introFrame = requestAnimationFrame(() => {
            if (!this.isConnected || version !== this.#mediaVersion || !this.hasAttribute('data-intro-pending')) return;
            this.removeAttribute('data-intro-pending');
            this.setAttribute('data-building', '');
            this.#introTimer = setTimeout(this.#finishIntro, 2900);
          });
        });
      };
      if ('requestIdleCallback' in window) this.#idle = requestIdleCallback(begin, { timeout: 400 });
      else this.#idleFallback = setTimeout(begin, 80);
    }).catch(failed);
  }

  async #decode(image) {
    if (!image) return;
    if (image.decode) await image.decode();
    else if (!image.complete) await new Promise((resolve, reject) => {
      image.addEventListener('load', resolve, { once: true, signal: this.#controller.signal });
      image.addEventListener('error', reject, { once: true, signal: this.#controller.signal });
    });
    if (!image.naturalWidth) throw new Error('Cover image unavailable');
  }

  #cancelIdle() {
    if (this.#idle !== null && 'cancelIdleCallback' in window) cancelIdleCallback(this.#idle);
    this.#idle = null;
    clearTimeout(this.#idleFallback);
  }

  #cancelReplay() {
    this.#replayVersion++;
    this.#replaying = false;
    this.#replayAnimations.forEach(animation => animation.cancel());
    this.#replayAnimations = [];
  }

  #replayIntro() {
    if (!this.#logo || this.hasAttribute('data-cover-loading') || this.hasAttribute('data-intro-pending') ||
        this.hasAttribute('data-flat') || this.dataset.animateLogo !== 'true' || this.#constrained ||
        this.#reduced.matches || document.hidden || !this.getAnimations) return;
    this.#finishIntro();
    this.#cancelReveal();
    // Commit removal of the old CSS clocks before adding the same state again,
    // including another click during an unfinished replay.
    this.getAnimations({ subtree: true });
    const version = this.#replayVersion;
    this.#replaying = true;
    this.#introFinished = false;
    const effects = this.querySelector('.product-overlay-hero__background-effects');
    if (this.#background && effects && Number(this.dataset.backgroundBlur) > 0) {
      this.#softBackground = this.#background.cloneNode();
      this.#softBackground.classList.add('product-overlay-hero__background-soft');
      this.#softBackground.alt = '';
      this.#softBackground.setAttribute('aria-hidden', 'true');
      effects.prepend(this.#softBackground);
    }
    // Read the existing CSS clocks once, before paint, and start at their
    // finished composition. No remount, skeleton, or additional asset request.
    this.setAttribute('data-building', '');
    this.#replayAnimations = this.getAnimations({ subtree: true }).filter(animation =>
      animation.animationName?.startsWith('bardem-'));
    const animations = this.#replayAnimations;
    const duration = Math.max(0, ...animations.map(animation => Number(animation.effect.getComputedTiming().endTime) || 0));
    if (!animations.length || !Number.isFinite(duration)) { this.#finishIntro(); return; }
    for (const animation of animations) {
      animation.pause();
      animation.currentTime = duration;
      animation.playbackRate = -2;
      animation.play();
    }
    this.#introTimer = setTimeout(this.#finishIntro, duration + 500);
    void Promise.all(animations.map(animation => animation.finished)).then(() => {
      if (!this.isConnected || version !== this.#replayVersion || !this.#replaying) return;
      for (const animation of animations) {
        animation.currentTime = 0;
        animation.playbackRate = 2;
        animation.play();
      }
      return Promise.all(animations.map(animation => animation.finished)).then(() => {
        if (version === this.#replayVersion && this.#replaying) this.#finishIntro();
      });
    }).catch(() => {
      if (version === this.#replayVersion && this.#replaying) this.#finishIntro();
    });
  }

  #markReady() {
    const wasLoading = this.hasAttribute('data-cover-loading');
    const skeleton = this.querySelector('[data-cover-skeleton]');
    const preview = skeleton?.querySelector('.store-skeleton');
    // Only crossfade a placeholder that actually painted. Cached loads should
    // not flash a newly visible skeleton just to perform the reveal.
    const previewVisible = wasLoading && preview && getComputedStyle(preview).visibility === 'visible';
    this.removeAttribute('data-cover-loading');
    this.setAttribute('aria-busy', 'false');
    if (wasLoading) this.#revealCover(previewVisible ? skeleton : null);
    if (this.#introFinished) this.dispatchEvent(new CustomEvent('hero:intro-settled', { bubbles: true }));
  }

  #cancelReveal() {
    this.#revealAnimations.forEach(animation => animation.cancel());
    this.#revealAnimations = [];
  }

  #revealCover(skeleton = null) {
    this.#cancelReveal();
    if (!this.isConnected || this.hasAttribute('data-cover-loading') || this.hasAttribute('data-flat') ||
        this.#constrained || this.#reduced.matches || document.hidden || !this.#canvas?.animate) return;
    // Opacity only: no extra layout, blur calculation, download, or readiness
    // gate. Underlying CSS stays visible if the animation is interrupted.
    const options = { duration: 600, easing: 'ease-out' };
    for (const layer of this.#canvas.querySelectorAll(':scope > .product-overlay-hero__background, :scope > [data-floating-logo]')) {
      this.#revealAnimations.push(layer.animate([{ opacity: 0 }, { opacity: 1 }], options));
    }
    if (skeleton) {
      this.#revealAnimations.push(skeleton.animate([
        { opacity: 1, visibility: 'visible' }, { opacity: 0, visibility: 'visible' },
      ], options));
    }
  }

  #finishIntro = () => {
    this.#cancelReplay();
    this.#introFinished = true;
    cancelAnimationFrame(this.#introFrame);
    clearTimeout(this.#introTimer);
    this.#cancelIdle();
    this.removeAttribute('data-intro-pending');
    this.removeAttribute('data-building');
    this.#softBackground?.remove();
    this.#softBackground = null;
    if (!this.hasAttribute('data-cover-loading')) this.dispatchEvent(new CustomEvent('hero:intro-settled', { bubbles: true }));
    if (this.#pendingFlat && this.isConnected) {
      this.#pendingFlat = false;
      this.#showFlat();
    }
  };

  #schedule = () => {
    if (this.#frame) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = 0;
      if (this.hasAttribute('data-flat') || !this.#logo) return;
      if (this.#reduced.matches || this.#constrained) {
        this.#logo.style.removeProperty('transform');
        this.#background?.style.removeProperty('transform');
        if (this.#reduced.matches) this.#finishIntro();
        this.#lastProgress = null;
        return;
      }
      const rect = this.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > innerHeight) return;
      const travel = Math.max(rect.height, 1);
      const progress = Math.min(Math.max(-rect.top / travel, 0), 1);
      const position = `${progress}:${travel}`;
      if (position === this.#lastProgress) return;
      this.#lastProgress = position;
      this.#logo.style.transform = `translate3d(0, ${-progress * travel * 0.55}px, 0)`;
      if (this.#background) this.#background.style.transform = `translate3d(0, ${progress * 12}%, 0)`;
      if (this.#softBackground) this.#softBackground.style.transform = this.#background.style.transform;
    });
  };
}

if (!customElements.get('product-overlay-hero')) customElements.define('product-overlay-hero', ProductOverlayHero);
